import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faMagnifyingGlass, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { useNavigate } from "react-router-dom";

import { InputAdornment } from "@mui/material";

import { Icon, PermissionCheck } from "@/components";

import { PermissionRoles } from "@/enum";
import { UserLanguage } from "@/enum/user.enum";

import { useHasPermissions } from "@/utils/useHasPermissions";
import { usePageTitle } from "@/utils/usePageTitle";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useAppSelector } from "@/redux/hooks";

import { ROUTE_CONFIG } from "@/routes/config";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { useLazyGetCoursesQuery } from "@/pages/Private/redux/courses/courses.api";
import { SortDirection, useSearch } from "@/utils";

import { InputField } from "@/components/InputField/InputField";

import { SelectCreatingCourseFlowModal } from "../../components/SelectCreatingCourseFlowModal";
import CoursesList from "../../components/CourseList";
import { Courses } from "../../schema/courses";

const { COURSES } = ROUTE_CONFIG;
const ALL_LANGUAGES = "all";
export const CoursesOverview: FunctionComponent = () => {
	const { t } = useTranslation();

	const ts = (key: string) => t(`courses.${key}`);
	const { hasPermissions } = useHasPermissions();

	const [isOpen, setIsOpen] = useState<boolean>(false);
	/** The reader's language filter; null lists every language (BCM360-342). */
	const [language, setLanguage] = useState<UserLanguage | null>(null);
	const { user } = useAppSelector(authSelector);
	const navigate = useNavigate();

	const { page, setSearchValue, query, searchValue, setSorting, sortColumn, sortOrder } =
		useSearch();

	useEffect(() => {
		if (!sortColumn && !sortOrder) {
			setSorting("date", SortDirection.ASC);
		}
	}, [setSorting, sortColumn, sortOrder]);

	const [data, setData] = useState<Courses[]>();

	const [getScenarios, { isLoading }] = useLazyGetCoursesQuery();

	useEffect(() => {
		const fetchMyScenarios = async () => {
			const cData = await getScenarios({
				page,
				limit: 100,
				searchValue: query,
				sortBy: sortColumn,
				sortOrder: sortOrder,
				...(language ? { language } : {}),
			}).unwrap();

			setData(cData.data);
		};

		if (sortColumn && sortOrder) {
			fetchMyScenarios();
		}
	}, [getScenarios, page, query, sortColumn, sortOrder, language]);

	usePageTitle(ts("title"));

	const handleCourses = useCallback(() => {
		const isUserCompanyActivated = user?.company?.activated;

		if (!!isUserCompanyActivated) {
			setIsOpen(true);

			return;
		} else {
			navigate(`${COURSES}/create`);
		}
	}, [user, navigate]);

	const sortingOptions = useMemo(() => {
		const options = [
			{ field: ts("sortingOptions.name"), direction: SortDirection.ASC },
			{ field: ts("sortingOptions.name"), direction: SortDirection.DESC },
			{ field: ts("sortingOptions.date"), direction: SortDirection.ASC },
			{ field: ts("sortingOptions.date"), direction: SortDirection.DESC },
		];

		return options.map((item, index) => {
			const itemIcon = item.direction === "DESC" ? "↑ " : "↓ ";

			return {
				id: index.toString(),
				itemIcon,
				title: `${itemIcon} ${item.field}`,
				fieldKey: item.field === ts("sortingOptions.name") ? "name" : "date",
				value: item.direction,
			};
		});
	}, [ts]);

	const languageOptions = useMemo(
		() => [
			{ id: ALL_LANGUAGES, title: ts("language.all") },
			{ id: UserLanguage.EN, title: ts("language.english") },
			{ id: UserLanguage.DE, title: ts("language.deutsch") },
		],
		[ts]
	);

	const selectedLanguage =
		languageOptions.find((option) => option.id === (language ?? ALL_LANGUAGES)) ??
		languageOptions[0];

	const selectedItem = useMemo(() => {
		const itemIcon = sortOrder === "DESC" ? "↑ " : "↓ ";

		const item = {
			id: `${sortColumn} - ${sortOrder}`,
			title: `${itemIcon} ${
				sortColumn === "name" ? ts("sortingOptions.name") : ts("sortingOptions.date")
			}`,
		};

		return item;
	}, [sortColumn, sortOrder, ts]);

	function handleSortSelect(field: string, direction: SortDirection) {
		setSorting(field, direction);
	}

	return (
		<>
			<div className="sm:block flex flex-1 flex-col items-start p-6">
				<div className="w-full flex items-center justify-between mb-6">
					<h3 className="text-primary-gray text-[14px] font-normal">
						{/* Only the Global Admin can create scenarios (BCM360-353, BCM360-358). */}
						{hasPermissions([PermissionRoles.GLOBAL_ADMIN])
							? ts("subtitle")
							: ts("subtitleChooseOnly")}
					</h3>
					<div className="flex gap-4">
						<PermissionCheck requiredPermissions={[PermissionRoles.USER]}>
							<>
								<div className="w-full">
									<Dropdown
										floating
										classNameButton="!font-medium text-[14px]"
										data={sortingOptions}
										defaultValue={selectedItem}
										handleSelect={(item) => {
											const selected = item as AutoCompleteItem & {
												fieldKey: string;
												value: SortDirection;
											};

											handleSortSelect(selected.fieldKey, selected.value);
										}}
										labelClassName="!font-medium !text-[14px]"
									/>
								</div>

								{/* Wide enough for the longest option — the menu is as wide as its button, and
								    "Alle" alone would clip "Englisch" / "Deutsch". */}
								<div className="w-full min-w-[150px]">
									<Dropdown
										floating
										classNameButton="!font-medium text-[14px]"
										data={languageOptions}
										handleSelect={(item) =>
											setLanguage(item.id === ALL_LANGUAGES ? null : (item.id as UserLanguage))
										}
										labelClassName="!font-medium !text-[14px]"
										value={selectedLanguage}
									/>
								</div>

								<InputField
									className="!h-[48px] !font-medium !text-[14px] min-w-[200px] !mb-0"
									handleChange={(e) => {
										setSearchValue(e.target.value);
									}}
									iconInside={
										<InputAdornment position="start">
											<Icon icon={faMagnifyingGlass} />
										</InputAdornment>
									}
									name="search"
									placeholder={`${ts("search")}...`}
									value={searchValue}
								/>
							</>
						</PermissionCheck>

						<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
							<Button
								className="!h-[44px] !font-medium !text-[14px] !w-fit"
								color={ButtonColor.ACTION}
								image={<Icon className="w-5 h-5 mr-2" icon={faPlus} />}
								size={ButtonSize.ML}
								title={ts("create.title")}
								onClick={handleCourses}
							></Button>
						</PermissionCheck>
					</div>
				</div>
				<PermissionCheck requiredPermissions={[PermissionRoles.USER]}>
					<CoursesList data={data ?? []} isLoading={isLoading} />
				</PermissionCheck>
			</div>
			<SelectCreatingCourseFlowModal isOpen={isOpen} setIsOpen={setIsOpen} />
		</>
	);
};
